import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Heart, MessageCircle, Send, Users, Award, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POST_TYPES = [
  { value: 'text', label: 'Share a thought', color: '#1A1A1A' },
  { value: 'celebration', label: 'Celebrate a win', color: '#2E8B57' },
  { value: 'question', label: 'Ask the community', color: '#4A6FA5' },
  { value: 'resource', label: 'Share a resource', color: '#C25E3E' },
];

const getTypeStyle = (type) => {
  const t = POST_TYPES.find(p => p.value === type);
  return t ? t.color : '#1A1A1A';
};

const getTypeLabel = (type) => {
  const labels = { celebration: 'CELEBRATION', question: 'QUESTION', resource: 'RESOURCE' };
  return labels[type] || null;
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const Initials = ({ name, avatar, size = 'w-10 h-10 text-sm' }) => {
  const init = name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  if (avatar) {
    return <img src={avatar} alt={name} className={`${size} rounded-full object-cover flex-shrink-0`} />;
  }
  return (
    <div className={`${size} rounded-full bg-[#C25E3E] text-white flex items-center justify-center flex-shrink-0`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
      {init}
    </div>
  );
};

const PostCard = ({ post, user, onLike, onComment }) => {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');
  const typeLabel = getTypeLabel(post.post_type);

  const submitComment = () => {
    if (!commentText.trim()) return;
    onComment(post.id, commentText);
    setCommentText('');
    setShowCommentInput(false);
  };

  return (
    <div className="bg-white border border-[#1A1A1A]/8 p-6 transition-all hover:shadow-md" data-testid={`post-${post.id}`}>
      {/* Author */}
      <div className="flex items-start gap-3 mb-4">
        <Initials name={post.author_name} avatar={post.author_avatar} />
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }}>{post.author_name}</p>
          <p className="text-[11px] text-[#888]" style={{ fontFamily: "'Outfit', sans-serif" }}>{post.author_headline || 'Community Member'} &middot; {timeAgo(post.created_at)}</p>
        </div>
        {typeLabel && (
          <span className="text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 border" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: getTypeStyle(post.post_type), borderColor: getTypeStyle(post.post_type) }}>
            {typeLabel}
          </span>
        )}
      </div>

      {/* Content */}
      <p className="text-[15px] leading-relaxed mb-5 whitespace-pre-wrap" style={{ fontFamily: "'Lora', serif", color: '#1A1A1A' }}>{post.content}</p>

      {/* Stats */}
      <div className="flex items-center gap-6 pt-4 border-t border-[#1A1A1A]/5">
        <button onClick={() => user ? onLike(post.id) : null} className={`flex items-center gap-1.5 text-[13px] transition-colors ${post.is_liked ? 'text-[#C25E3E]' : 'text-[#888] hover:text-[#C25E3E]'}`} style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`like-${post.id}`}>
          <Heart size={16} fill={post.is_liked ? '#C25E3E' : 'none'} /> {post.like_count || 0}
        </button>
        <button onClick={() => user ? setShowCommentInput(!showCommentInput) : null} className="flex items-center gap-1.5 text-[13px] text-[#888] hover:text-[#4A6FA5] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`comment-btn-${post.id}`}>
          <MessageCircle size={16} /> {post.comment_count || 0}
        </button>
      </div>

      {/* Comments */}
      {post.comments?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#1A1A1A]/5 space-y-3">
          {post.comments.map(c => (
            <div key={c.id} className="flex gap-2">
              <Initials name={c.author_name} size="w-7 h-7 text-[10px]" />
              <div className="bg-[#FAF9F6] px-3 py-2 flex-1">
                <p className="text-[12px] font-medium text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.author_name}</p>
                <p className="text-[13px] text-[#4A4A4A]" style={{ fontFamily: "'Lora', serif" }}>{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comment input */}
      {showCommentInput && (
        <div className="mt-4 flex gap-2">
          <input value={commentText} onChange={e => setCommentText(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitComment()} placeholder="Write a comment..." className="flex-1 h-10 px-4 text-[13px] border border-[#1A1A1A]/10 bg-[#FAF9F6] outline-none focus:border-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`comment-input-${post.id}`} />
          <button onClick={submitComment} className="h-10 px-4 bg-[#C25E3E] text-white hover:bg-[#1A1A1A] transition-colors" data-testid={`comment-submit-${post.id}`}><Send size={14} /></button>
        </div>
      )}
    </div>
  );
};

const CommunityPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostType, setNewPostType] = useState('text');
  const [posting, setPosting] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchMembers();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/community/posts`, { withCredentials: true });
      setPosts(res.data.posts);
    } catch { } finally { setLoadingPosts(false); }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/community/members`);
      setMembers(res.data.members.slice(0, 8));
      setTotalMembers(res.data.total);
    } catch { }
  };

  const createPost = async () => {
    if (!newPostContent.trim() || !user) return;
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/community/posts`, { content: newPostContent, post_type: newPostType }, { withCredentials: true });
      setNewPostContent('');
      setNewPostType('text');
      fetchPosts();
    } catch { } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API_URL}/api/community/posts/${postId}/like`, {}, { withCredentials: true });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_liked: res.data.action === 'liked', like_count: res.data.like_count } : p));
    } catch { }
  };

  const handleComment = async (postId, content) => {
    try {
      const res = await axios.post(`${API_URL}/api/community/posts/${postId}/comment`, { content }, { withCredentials: true });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: [...(p.comments || []), res.data], comment_count: (p.comment_count || 0) + 1 } : p));
    } catch { }
  };

  return (
    <div className="min-h-screen bg-[#F3EFE9] pt-32 pb-20" data-testid="community-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[12px] tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>
            THE COMMUNITY
          </p>
          <h1 className="text-4xl lg:text-5xl tracking-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>
            Connect. Inspire. Build Together.
          </h1>
          <p className="text-base text-[#4A4A4A] max-w-xl mx-auto" style={{ fontFamily: "'Lora', serif" }}>
            A space for leaders, builders, and changemakers to share stories, celebrate wins, and support each other.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* MAIN FEED */}
          <div className="lg:col-span-8">
            {/* Create Post */}
            {user ? (
              <div className="bg-white border border-[#1A1A1A]/8 p-6 mb-6" data-testid="create-post">
                <div className="flex items-start gap-3 mb-4">
                  <Initials name={user.name} />
                  <div className="flex-1">
                    <textarea
                      value={newPostContent}
                      onChange={e => setNewPostContent(e.target.value)}
                      placeholder="Share something with the community..."
                      className="w-full min-h-[100px] px-4 py-3 text-[14px] border border-[#1A1A1A]/10 bg-[#FAF9F6] outline-none focus:border-[#C25E3E] resize-none transition-colors"
                      style={{ fontFamily: "'Lora', serif" }}
                      data-testid="post-textarea"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex gap-2">
                        {POST_TYPES.map(t => (
                          <button key={t.value} onClick={() => setNewPostType(t.value)} className={`text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border transition-all ${newPostType === t.value ? 'border-[#C25E3E] bg-[#C25E3E] text-white' : 'border-[#1A1A1A]/10 text-[#888] hover:border-[#C25E3E] hover:text-[#C25E3E]'}`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }} data-testid={`post-type-${t.value}`}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <button onClick={createPost} disabled={posting || !newPostContent.trim()} className="h-10 px-6 bg-[#C25E3E] text-white text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] transition-all disabled:opacity-50" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="submit-post">
                        {posting ? 'Posting...' : 'Post'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-[#1A1A1A]/8 p-8 mb-6 text-center" data-testid="join-cta">
                <Sparkles size={32} className="mx-auto mb-4 text-[#C25E3E]" />
                <h3 className="text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Join the Conversation</h3>
                <p className="text-[14px] text-[#4A4A4A] mb-6" style={{ fontFamily: "'Lora', serif" }}>Create your free account to post, comment, and connect with the community.</p>
                <Link to="/auth" className="inline-flex items-center h-12 px-8 bg-[#C25E3E] text-white text-[12px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="join-community-btn">
                  Join Free <ArrowRight size={14} className="ml-2" />
                </Link>
              </div>
            )}

            {/* Posts Feed */}
            <div className="space-y-4">
              {loadingPosts ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="bg-white border border-[#1A1A1A]/8 p-6 animate-pulse">
                    <div className="flex gap-3 mb-4"><div className="w-10 h-10 rounded-full bg-[#F3EFE9]" /><div className="flex-1 space-y-2"><div className="h-4 bg-[#F3EFE9] w-1/3" /><div className="h-3 bg-[#F3EFE9] w-1/4" /></div></div>
                    <div className="h-16 bg-[#F3EFE9]" />
                  </div>
                ))
              ) : posts.length === 0 ? (
                <div className="bg-white border border-[#1A1A1A]/8 p-12 text-center">
                  <MessageCircle size={40} className="mx-auto mb-4 text-[#1A1A1A]/20" />
                  <h3 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Be the first to post!</h3>
                  <p className="text-[14px] text-[#888]" style={{ fontFamily: "'Lora', serif" }}>Start a conversation that inspires the community.</p>
                </div>
              ) : (
                posts.map(post => <PostCard key={post.id} post={post} user={user} onLike={handleLike} onComment={handleComment} />)
              )}
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-4 space-y-6">
            {/* Community Stats */}
            <div className="bg-white border border-[#1A1A1A]/8 p-6" data-testid="community-stats">
              <h3 className="text-[11px] tracking-[0.2em] uppercase mb-5" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>Community</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-[#FAF9F6]">
                  <p className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totalMembers}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#888]" style={{ fontFamily: "'Outfit', sans-serif" }}>Members</p>
                </div>
                <div className="text-center p-4 bg-[#FAF9F6]">
                  <p className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{posts.length}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#888]" style={{ fontFamily: "'Outfit', sans-serif" }}>Posts</p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-white border border-[#1A1A1A]/8 p-6" data-testid="community-benefits">
              <h3 className="text-[11px] tracking-[0.2em] uppercase mb-5" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>Member Benefits</h3>
              <ul className="space-y-3">
                {[
                  { icon: Users, text: 'Connect with 370+ verified leaders' },
                  { icon: Award, text: 'Free access to 40 executive courses' },
                  { icon: Sparkles, text: 'Early access to Edition 2 chapters' },
                  { icon: MessageCircle, text: 'Join discussions & ask questions' },
                  { icon: Heart, text: 'Nominate & celebrate women builders' },
                ].map((b, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <b.icon size={16} className="text-[#C25E3E] flex-shrink-0" />
                    <span className="text-[13px] text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif" }}>{b.text}</span>
                  </li>
                ))}
              </ul>
              {!user && (
                <Link to="/auth" className="block mt-5 text-center h-11 leading-[44px] bg-[#C25E3E] text-white text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                  Join Free
                </Link>
              )}
            </div>

            {/* Recent Members */}
            <div className="bg-white border border-[#1A1A1A]/8 p-6" data-testid="recent-members">
              <h3 className="text-[11px] tracking-[0.2em] uppercase mb-5" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>Recent Members</h3>
              <div className="space-y-3">
                {members.map(m => (
                  <div key={m.id} className="flex items-center gap-3">
                    <Initials name={m.name} size="w-8 h-8 text-[10px]" />
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-[#1A1A1A] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.name}</p>
                      <p className="text-[10px] text-[#888] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.headline || m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
